import React, { useState, useEffect } from "react";
import KnowtexEMSuggestion from "./KnowtexEMSuggestion";
import { calculateLevels } from "./emlevelsfcn";
import sendServerLogging from "../../../utils/sendServerLogging";
import userJSON from "../../../utils/userJSON";

export default function EMSection({
  isRationaleProvided,
  setIsRationaleProvided,
  mdm_selected,
  setMDMSelected,
  mdm_suggested,
  userRationale,
  setUserRationale,
  showRationale,
  setShowRationale,
  setFinalSelectedLevel,
  patient,
  setRecommendedLevel,
  setFinalSelectedTs,
  setMDMSelectedTs,
  encounterTime,
  setEncounterTime,
  status,
  emData,
  setEMData,
  setNote,
}) {
  const [activeButton, setActiveButton] = useState(null);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [levels, setLevels] = useState({});
  const [timeModified, setTimeModified] = useState(false);

  // Map patient type to visit type display
  const getVisitTypeFromPatientType = (patientType) => {
    switch (patientType) {
      case "new":
        return "New Patient";
      case "fu":
        return "Follow Up";
      case "telemed":
        return "Telemedicine";
      default:
        return "New Patient";
    }
  };

  useEffect(() => {
    const lvls = calculateLevels(emData.mdm);
    setLevels(lvls);
    // Store Knowtex's recommended numeric level in the parent:
    if (lvls.note) {
      setRecommendedLevel(lvls.note);
    }

    // Pre-populate encounter time from emData if available and not already set
    if (emData?.time) {
      setEncounterTime(emData.time);
    }
  }, [emData, setRecommendedLevel, encounterTime, setEncounterTime]);

  // This is the FIRST row of level buttons
  const handleClick = (selectedLevel) => {
    // Enhanced server logging with emData
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "E&M_SELECTED_CODE_CLICKED",
        eventData: {
          mrn: patient.mrn,
          em_code: selectedLevel,
          recommended_code: emData?.e_and_m_code,
          selection_basis: emData?.selection_on,
        },
        patientId: patient.patient_id,
      },
    });

    const timestampInSeconds = Math.floor(Date.now() / 1000);

    setActiveButton(selectedLevel);
    setMDMSelected(selectedLevel);
    setMDMSelectedTs(timestampInSeconds);

    // If user picks the same as recommended => skip second row, set final
    if (parseInt(selectedLevel) === parseInt(levels.note)) {
      setShowRecommendations(false);
      setShowRationale(false);
      setIsRationaleProvided(true);
      setFinalSelectedLevel(selectedLevel);
      setFinalSelectedTs(timestampInSeconds);
    } else {
      // If user picks a different level => reveal second row
      setShowRecommendations(true);
      setShowRationale(false);
      setIsRationaleProvided(false);
      //setFinalSelectedLevel(null);
      setFinalSelectedTs(null);
    }
  };

  const getVisitType = () => {
    if (emData?.patient_type) {
      return getVisitTypeFromPatientType(emData.patient_type);
    }
    return "New Patient";
  };

  return (
    <div className="flex flex-col">
      {/* Header row with E&M Codes title and Time field */}
      <div className="flex flex-row justify-between items-start mb-4">
        <div className="flex flex-col flex-1">
          <h1 className="text-accent1 text-xl font-medium mb-2">E&M Codes</h1>
          <h2 className="text-light-grey text-md font-medium mb-2">
            Visit Type: {getVisitType()}
          </h2>
        </div>

        {/* Time field aligned with E&M Codes header */}
        <div className="flex flex-col min-w-[300px]">
          <div className="mb-4">
            <label className="block text-accent1 text-sm font-medium mb-2">
              Total Time Spent (minutes)
            </label>
            <input
              type="number"
              readOnly={status === "Locked"}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-green focus:border-transparent bg-secondary-grey text-black"
              defaultValue={emData?.time ? Math.floor(emData.time) : ""}
              onChange={(e) => {
                const newTime = e.target.value;
                // Mark as modified when user changes the value
                setTimeModified(true);
                // Update emData.time directly
                if (setEMData && emData) {
                  setEMData({
                    ...emData,
                    time: newTime === "" ? 0 : parseFloat(newTime) || 0,
                  });
                }
                // Also update encounterTime for backward compatibility
                if (setEncounterTime) {
                  setEncounterTime(newTime);
                }
              }}
              placeholder="Enter time in minutes"
              min="0"
              step="1"
            />
            {timeModified && (
              <button
                type="button"
                onClick={() => {
                  if (setNote && emData?.time) {
                    const timeMinutes = Math.floor(emData.time);
                    const timeStatement = `\n\nTotal minutes of encounter: ${timeMinutes}\nThis time includes time spent with the patient as well as time spent documenting in the medical record, reviewing patient's records and tests, obtaining history, placing orders, communicating with other healthcare professionals, counseling the patient, family, or caregiver, and/or care coordination for the diagnoses above.`;

                    setNote((prevNote) => (prevNote || "") + timeStatement);
                    // Reset the modified state after insertion
                    setTimeModified(false);
                  }
                }}
                className="mt-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                Insert time statement into note
              </button>
            )}
          </div>
        </div>
      </div>

      {/* E&M selection content */}
      <div className="flex flex-col">
        <h3 className="text-accent1 text-sm font-light italic mb-2">
          Select your level of service choice to see Knowtex's recommendation.
        </h3>
        <h3 className="text-accent1 text-sm font-semibold">Level:</h3>

        {/* FIRST row of level buttons */}
        <div className="flex flex-row gap-4">
          {[2, 3, 4, 5].map((number) => (
            <button
              key={number}
              onClick={() => handleClick(number)}
              className={`border-accent1 border rounded-md hover:bg-transparent-purple 
                hover:text-accent1 text-accent1 font-bold py-2 px-4 w-12 h-12 
                flex items-center justify-center ${
                  activeButton === number ? "bg-transparent-purple" : ""
                } `}
            >
              {number}
            </button>
          ))}
        </div>

        {/* If user picks a different level, show second row */}
        {showRecommendations && emData && (
          <KnowtexEMSuggestion
            showRecommendations={showRecommendations}
            isRationaleProvided={isRationaleProvided}
            setIsRationaleProvided={setIsRationaleProvided}
            userRationale={userRationale}
            setUserRationale={setUserRationale}
            showRationale={showRationale}
            setShowRationale={setShowRationale}
            mdm_suggested={mdm_suggested}
            setFinalSelectedLevel={setFinalSelectedLevel}
            setFinalSelectedTs={setFinalSelectedTs}
            levels={levels}
            patient={patient}
            emData={emData}
          />
        )}
      </div>
    </div>
  );
}
